import React, { useEffect, useState } from 'react';
import { styled as muiStyled } from '@mui/material/styles';
import { Box, Tooltip } from '@mui/material';
import { bg, dark } from 'styles/theme';

const TabsContainer = muiStyled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
    padding: '6px',
    background: 'white',
    borderRadius: '12px',
}));

const Separator = muiStyled(Box)(() => ({
    width: '1px',
    height: '22px',
    background: '#D8E6F0',
    flexShrink: 0,
}));

const TabItem = muiStyled(Box, { shouldForwardProp: (p) => p !== 'selected' })(({ disabled, selected }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '8px',
    background: selected ? bg : 'transparent',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s ease',
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',

    '&:hover': selected ? {} : {
        background: '#F5F9FF',
    },

    '& .tab-label': {
        fontFamily: 'Calibri, sans-serif',
        fontSize: '16px',
        // fontWeight: selected ? 700 : 400,
        fontWeight: 400,

        color: dark,
        // lineHeight: 'normal',
        lineHeight: '25px',
        textAlign: 'right',
    },

    '& .tab-icon': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: dark,
        fontSize: '12px',
        flexShrink: 0,
        '& i': {
            fontSize: 'inherit',
            color: 'inherit',
        },
    },
}));

const IconTabs = ({ tabs = [], defaultValue = 0, handleTab, value: controlledValue }) => {
    const [internalValue, setInternalValue] = useState(defaultValue);

    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;

    useEffect(() => {
        if (!isControlled && typeof defaultValue === 'number') {
            setInternalValue(defaultValue);
        }
    }, [defaultValue]);

    const handleClick = (index, disabled) => {
        if (disabled) return;
        if (!isControlled) setInternalValue(index);
        handleTab?.(index);
    };

    return (
        <TabsContainer>
            {tabs.map((tab, index) => (
                <React.Fragment key={index}>
                    {index > 0 && index !== value && index !== value + 1 && <Separator />}
                    <Tooltip
                        title={tab.tooltip || ''}
                        placement="top"
                        disableHoverListener={!tab.tooltip}
                    >
                        <TabItem
                            selected={value === index ? 1 : 0}
                            disabled={tab.disabled ? 1 : 0}
                            onClick={() => handleClick(index, tab.disabled)}
                            role="tab"
                            aria-selected={value === index}
                            aria-disabled={tab.disabled}
                            tabIndex={tab.disabled ? -1 : 0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') handleClick(index, tab.disabled);
                            }}
                        >
                            {tab.icon && <span className="tab-icon">{tab.icon}</span>}
                            {tab.label && <span className="tab-label">{tab.label}</span>}
                        </TabItem>
                    </Tooltip>
                </React.Fragment>
            ))}
        </TabsContainer>
    );
};

export default IconTabs;
